import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Plus, FileText, Download, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const KIND_LABEL = { policy: "Policy", offer_letter: "Offer Letter", id_proof: "ID Proof", salary_letter: "Salary Letter", other: "Other" };

export default function DocumentsPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", kind: "policy", employee_id: "", file_data_url: "", note: "" });
  const [employees, setEmployees] = useState([]);

  const load = async () => {
    try { const { data } = await api.get("/documents"); setRows(data); }
    catch (e) { toast.error("Failed"); } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    if (user.role === "company_admin") api.get("/employees").then(({data}) => setEmployees(data)).catch(() => {});
  }, [user.role]);

  const readFile = (file) => new Promise((res, rej) => {
    const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.file_data_url) return toast.error("Please attach a file");
    try {
      await api.post("/documents", { ...form, employee_id: form.employee_id || null });
      toast.success("Document uploaded"); setOpen(false);
      setForm({ title: "", kind: "policy", employee_id: "", file_data_url: "", note: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Failed")); }
  };

  const download = async (id, title) => {
    try {
      const { data } = await api.get(`/documents/${id}`);
      const a = document.createElement("a"); a.href = data.file_data_url; a.download = title || "document"; a.click();
    } catch (e) { toast.error("Failed to download"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this document?")) return;
    await api.delete(`/documents/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div>
      <PageHeader eyebrow="Library" title="Documents." testid="page-documents"
        description={user.role === "employee" ? "Company policies and documents shared with you." : "Upload policies, offer letters and per-employee documents."}
        actions={user.role === "company_admin" && (
          <button onClick={() => setOpen(true)} data-testid="btn-upload-doc"
            className="inline-flex items-center gap-2 bg-neutral-950 text-[#D4B87D] px-4 py-2.5 font-mono text-sm hover:bg-[#C9A961] hover:text-neutral-950 border border-neutral-950 hover:border-[#C9A961] transition-colors">
            <Plus className="w-4 h-4" /> Upload document
          </button>
        )}
      />
      <div className="p-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="documents-grid">
          {loading && <div className="font-mono text-xs text-neutral-500">Loading…</div>}
          {!loading && rows.length === 0 && <div className="col-span-full border border-dashed border-[#C9A961] p-10 text-center font-mono text-sm text-neutral-500"><FileText className="w-8 h-8 mx-auto mb-3 text-[#8C6F1E]" /> No documents yet.</div>}
          {rows.map((d) => (
            <div key={d.id} data-testid={`doc-${d.id}`} className="border border-neutral-200 bg-white p-6 hover:border-[#C9A961] transition-colors">
              <FileText className="w-6 h-6 text-[#8C6F1E] mb-3" strokeWidth={1.5} />
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#8C6F1E]">{KIND_LABEL[d.kind]}</div>
              <div className="font-serif font-bold text-lg tracking-tight mt-1">{d.title}</div>
              <div className="text-xs text-neutral-500 font-mono mt-1">{d.employee_id ? "Personal" : "Company-wide"} · {d.created_at?.slice(0, 10)}</div>
              {d.note && <p className="text-xs text-neutral-600 mt-2">{d.note}</p>}
              <div className="mt-4 flex items-center gap-2">
                <button onClick={() => download(d.id, d.title)} data-testid={`dl-${d.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#C9A961] font-mono text-xs hover:bg-neutral-950 hover:text-[#D4B87D] transition-colors">
                  <Download className="w-3.5 h-3.5" /> Download
                </button>
                {user.role === "company_admin" && (
                  <button onClick={() => remove(d.id)} className="text-neutral-400 hover:text-[#B33A1F] ml-auto"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-neutral-950/70 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white border border-[#C9A961] w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-neutral-200 px-6 py-4 flex items-center justify-between">
              <div><div className="text-[10px] font-mono uppercase tracking-widest text-[#8C6F1E]">Upload</div><div className="font-serif font-bold text-2xl tracking-tight">New document</div></div>
              <button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <input required value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} data-testid="doc-title" placeholder="Title"
                className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#C9A961] outline-none font-serif text-lg" />
              <div className="grid grid-cols-2 gap-4">
                <select value={form.kind} onChange={(e) => setForm({...form, kind: e.target.value})} data-testid="doc-kind" className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#C9A961] outline-none font-mono text-sm bg-white">
                  {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <select value={form.employee_id} onChange={(e) => setForm({...form, employee_id: e.target.value})} data-testid="doc-employee" className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#C9A961] outline-none font-mono text-sm bg-white">
                  <option value="">Company-wide</option>
                  {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                </select>
              </div>
              <input type="file" required accept="application/pdf,image/*,.doc,.docx" data-testid="doc-file"
                onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; if (f.size > 3000000) { toast.error("Max 3MB"); return; } setForm({...form, file_data_url: await readFile(f)}); }}
                className="text-xs font-mono block w-full" />
              {form.file_data_url && <div className="text-xs text-[#8C6F1E] font-mono">Attached ({Math.round(form.file_data_url.length/1024)} KB)</div>}
              <textarea rows={2} value={form.note} onChange={(e) => setForm({...form, note: e.target.value})} data-testid="doc-note" placeholder="Optional note"
                className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#C9A961] outline-none font-mono text-sm" />
              <button type="submit" data-testid="btn-upload-submit" className="w-full px-5 py-3 bg-neutral-950 text-[#D4B87D] font-mono text-sm hover:bg-[#C9A961] hover:text-neutral-950 border border-neutral-950 hover:border-[#C9A961] transition-colors">Upload</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
